// @ts-strict-ignore
import React, { createContext, useContext, useMemo, useState } from 'react';
import type { Dispatch, ReactNode, SetStateAction } from 'react';

import { useResponsive } from '@actual-app/components/hooks/useResponsive';

import { useGlobalPref } from '#hooks/useGlobalPref';

type SidebarContextValue = {
  collapsed: boolean;
  setCollapsed: (collapsed: boolean) => void;
  hidden: boolean;
  setHidden: Dispatch<SetStateAction<boolean>>;
  floating: boolean;
  alwaysFloats: boolean;
};

const SidebarContext = createContext<SidebarContextValue>(null);

type SidebarProviderProps = {
  children: ReactNode;
};

export function SidebarProvider({ children }: SidebarProviderProps) {
  const [floatingSidebar, setFloatingSidebarPref] =
    useGlobalPref('floatingSidebar');
  const [localCollapsed, setLocalCollapsed] = useState<boolean | null>(null);
  const [hidden, setHidden] = useState(false);
  const { width } = useResponsive();
  const alwaysFloats = width < 668;
  const collapsed =
    localCollapsed !== null ? localCollapsed : Boolean(floatingSidebar);

  const setCollapsed = (newCollapsed: boolean) => {
    setLocalCollapsed(newCollapsed);
    setFloatingSidebarPref(newCollapsed);
  };
  const floating = collapsed || alwaysFloats;

  return (
    <SidebarContext.Provider
      value={{
        collapsed,
        setCollapsed,
        hidden,
        setHidden,
        floating,
        alwaysFloats,
      }}
    >
      {children}
    </SidebarContext.Provider>
  );
}

const noop = () => {
  // No-op fallback when used outside of SidebarProvider
};

export function useSidebar() {
  const context = useContext(SidebarContext);

  return useMemo(
    () => ({
      collapsed: context?.collapsed ?? false,
      setCollapsed: context?.setCollapsed ?? noop,
      hidden: context?.hidden ?? false,
      setHidden: context?.setHidden ?? noop,
      floating: context?.floating ?? false,
      alwaysFloats: context?.alwaysFloats ?? false,
    }),
    [context],
  );
}
